/**
 * Query keys for a single loan's caches.
 *
 * Shared so every surface that writes to a deal invalidates the same set. The
 * deal inspector and the deal-file dialog both edit the same record; if they
 * kept private copies of these keys, editing in one would leave the other
 * showing a stale value until a reload.
 */

import type { QueryClient } from '@tanstack/react-query'

export const LOAN_QUERY_KEYS = {
  loans: ['loans'] as const,
  fieldLineage: (id: string) => ['loan-field-lineage', id] as const,
  fieldHistory: (id: string) => ['loan-field-history', id] as const,
  provenance: (id: string) => ['loan-provenance', id] as const,
  activity: (id: string) => ['loan-activity', id] as const,
  stageAudit: (id: string) => ['loan', id, 'stage-audit'] as const,
  fieldMap: (id: string) => ['loan', id, 'field-map'] as const,
  calculations: (id: string) => ['loan-calculations', id] as const,
  killReasons: ['loan-kill-reasons'] as const,
  /** The served origination stage model (`GET /loans/stages`). */
  stageModel: ['loan-stage-model'] as const,
  dealFile: (id: string) => ['deal-file', id] as const,
  /** The asset, sponsor, comps and the settled address behind one deal. The
   *  Property tab, the header address and the map pin all read it, and an
   *  address edit rewrites the property master it carries — so a save that
   *  leaves it cached shows the old address for the rest of its stale time. */
  dossierContext: (id: string) => ['loan', id, 'dossier-context'] as const,
  dealFieldCatalogue: ['deal-field-catalogue'] as const,
}

export async function invalidateLoanQueries(queryClient: QueryClient, loanId: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.loans }),
    queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.fieldLineage(loanId) }),
    queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.fieldHistory(loanId) }),
    queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.provenance(loanId) }),
    queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.activity(loanId) }),
    // A saved field is a stage-audit entry: the Audit trail and its badge.
    queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.stageAudit(loanId) }),
    // A saved field changes what the deal holds: the Field Map's score and worklist.
    queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.fieldMap(loanId) }),
    queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.calculations(loanId) }),
    queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.dealFile(loanId) }),
    // A saved address field rewrites the property master (address, coordinate,
    // provenance) and the settled address block. Left out of this list, the
    // dossier kept showing the address it had cached for five minutes.
    queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.dossierContext(loanId) }),
  ])
}
